import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useNoticias, useDeleteNoticia } from '@/hooks/useNoticias'
import {
    Plus, Search, Loader2, Bell, Edit, Trash2,
    ChevronLeft, Image as ImageIcon, CheckCircle2, XCircle
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { NoticiaForm } from '@/components/admin/NoticiaForm'
import type { NoticiaPortal } from '@/types/database'

export function NoticiasPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [showForm, setShowForm] = useState(false)
    const [editing, setEditing] = useState<NoticiaPortal | null>(null)

    useEffect(() => { setPageTitle('Notícias & Avisos') }, [setPageTitle])

    const { data: noticias, isLoading } = useNoticias()
    const deleteMut = useDeleteNoticia()

    const filtered = noticias?.filter(n =>
        n.titulo.toLowerCase().includes(search.toLowerCase()) ||
        n.conteudo.toLowerCase().includes(search.toLowerCase())
    ) || []

    const handleDelete = (id: string) => {
        if (confirm('Tem certeza que deseja remover esta notícia?')) {
            deleteMut.mutate(id)
        }
    }

    const handleEdit = (noticia: NoticiaPortal) => {
        setEditing(noticia)
        setShowForm(true)
    }

    const handleCreate = () => {
        setEditing(null)
        setShowForm(true)
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/admin/notificacoes" className="p-2 rounded-lg hover:bg-surface-100 text-surface-600 transition-colors">
                        <ChevronLeft size={20} />
                    </Link>
                    <div>
                        <h2 className="text-xl font-bold text-surface-900">Notícias do Portal</h2>
                        <p className="text-sm text-surface-700/60 mt-0.5">Gerencie os conteúdos visíveis aos associados</p>
                    </div>
                </div>
                <button onClick={handleCreate} className="btn btn-primary gap-2">
                    <Plus size={18} /> Nova Notícia
                </button>
            </div>

            {/* List */}
            <div className="glass-card">
                <div className="p-4 border-b border-surface-100 flex gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                        <input
                            type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Buscar notícias..."
                            className="input pl-10"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {isLoading ? (
                        <div className="flex justify-center p-12">
                            <Loader2 size={24} className="animate-spin text-primary-600" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-16 text-surface-700/60">
                            <Bell size={40} className="mx-auto mb-3 opacity-20" />
                            <p>Nenhuma notícia encontrada</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-surface-50/50 text-surface-700/60 uppercase text-[10px] font-bold tracking-widest">
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Imagem</th>
                                    <th className="px-6 py-4">Título / Tipo</th>
                                    <th className="px-6 py-4">Ordem</th>
                                    <th className="px-6 py-4 text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-100 text-sm">
                                {filtered.map(noticia => (
                                    <tr key={noticia.id} className="hover:bg-surface-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            {noticia.ativo ? (
                                                <span className="flex items-center gap-1.5 text-green-600 font-semibold text-[10px] uppercase">
                                                    <CheckCircle2 size={12} /> Ativo
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1.5 text-surface-400 font-semibold text-[10px] uppercase">
                                                    <XCircle size={12} /> Inativo
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {noticia.imagem_url ? (
                                                <img src={noticia.imagem_url} className="w-12 h-12 rounded-lg object-cover bg-surface-100" />
                                            ) : (
                                                <div className="w-12 h-12 rounded-lg bg-surface-100 flex items-center justify-center text-surface-400">
                                                    <ImageIcon size={20} />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="font-bold text-surface-900">{noticia.titulo}</p>
                                            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border
                                                ${noticia.tipo === 'aviso' ? 'bg-orange-50 text-orange-600 border-orange-100' :
                                                    noticia.tipo === 'noticia' ? 'bg-blue-50 text-blue-600 border-blue-100' :
                                                        'bg-surface-100 text-surface-600 border-surface-200'}`}>
                                                {noticia.tipo}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 font-mono text-surface-700/60">
                                            #{noticia.ordem}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => handleEdit(noticia)}
                                                    className="p-2 rounded-lg hover:bg-surface-100 text-surface-700/60 hover:text-primary-600 transition-all">
                                                    <Edit size={16} />
                                                </button>
                                                <button onClick={() => handleDelete(noticia.id)}
                                                    className="p-2 rounded-lg hover:bg-danger-50 text-surface-700/60 hover:text-danger-600 transition-all">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Modal Form */}
            {showForm && (
                <NoticiaForm
                    noticia={editing}
                    onClose={() => setShowForm(false)}
                />
            )}
        </div>
    )
}
